import _ from 'lodash';

import ControlPanel from './controls/ControlPanel';
import MobileControlPanel from './controls/mobile/ControlPanel';
import PlayButton from './controls/mobile/PlayButton';
import MenuPanel from './controls/mobile/MenuPanel.js';
import Slots from './controls/Slots.js';
import DebugPanel from './controls/DebugPanel';
import VerticalNumericStepper from './controls/VerticalNumericStepper';
import NumericStepper from './controls/NumericStepper';
import StateButton from './controls/StateButton';

import HelpPopup from './popups/HelpPopup';
import BigWinPopup from './popups/BigWinPopup';
import BonusSpinPopup from './popups/BonusSpinPopup';
import EndBonusRoundPopup from './popups/EndBonusRoundPopup';

import Footer from './controls/Footer.js';
import Config from './utils/Config.js';
import Utils from './utils/Utils.js';
import GameController from './controllers/GameController.js';

import PopupManager from './utils/PopupManager.js';
import WinPanel from './controls/WinPanel.js';
import TopButtonsPanel from './controls/TopButtonsPanel.js';
import SoundManager from './utils/SoundManager.js';
import LocaleManager from './utils/LocaleManager.js';

import OrientationHandler from './controls/OrientationHandler.js';
import BonusPanel from './controls/BonusPanel';

export default class Game {

    create()
    {
        window.game = this;

        // SIGNALS
        this.onPlay = new Phaser.Signal();
        this.onStopAutoSpins = new Phaser.Signal();
        this.onChangeBet = new Phaser.Signal();
        this.onChangeAutospins = new Phaser.Signal();
        this.onGoToLobby = new Phaser.Signal();

        this.game.input.maxPointers = 1;

        // setup controller
        const gameController = this._gameController = new GameController(this);

        PopupManager.init(this.game);

        SoundManager.playMusic('music/bg-music');

        gameController.initialize();

        this.game.onResume.add(() =>
        {
            if ((this._orientationHandler && this._orientationHandler.isShown) || leanderGMApi.isGUIBlocked())
            {
               this.game.paused = true;
            }
        }, this, Number.MAX_SAFE_INTEGER);
    }

    strokeLine(line)
    {
        return this._slots.strokeLine(line);
    }
    
    strokeLines(lines, time = 0)
    {
        return this._slots.strokeLines(lines, time);
    }
    
    hideLines()
    {
        return this._slots.hideLines();       
    }
    
    animateStrokeLines(lines)
    {
        return this._slots.animateStrokeLines(lines);
    }
    
    stopStrokeLinesAnimation()
    {
        return this._slots.stopStrokeLinesAnimation();   
    }
    
    strokeCells(cellModels)
    {
        return this._slots
            .strokeCells(cellModels)
            .then(() =>
            {
                console.log('Stroke cells complete.');
                return Promise.resolve();
            });
    }

    setCells(cellModels)
    {
        return new Promise((res) =>
        {
            this._slots.setCells(cellModels);
            console.log('Set cells complete.');
            res();
        });
    }
    
    spinTo(cellModels, extraSpinSlotIndexes = [])
    {
        return this._slots
            .spinTo(cellModels, extraSpinSlotIndexes)
            .then(() =>
            {
                console.log('Spin complete.');
                return Promise.resolve();
            });
    }

    transformTo(cellModels)
    {
        return this._slots.transformTo(cellModels);
    }

    highlightCells(cellModels)
    {
        return this._slots
            .highlightCells(cellModels)
            .then(() =>
            {
                console.log('Highlight complete');
                return Promise.resolve();
            });
    }

    nudging()
    {
        return this._slots
            .nudging()
            .then(() =>
            {
                console.log('Nudging complete');
                return Promise.resolve();
            });
    }

    ready()
    {
        this.reload();

        if (Config.MobileDevice)
        {
            const orientation = window.innerWidth > window.innerHeight ? Config.LANDSCAPE : Config.PORTRAIT;
            const orientationHandler = this._orientationHandler = new OrientationHandler(this);
            console.log('orientation', orientation, Config.START_ORIENTATION);
            Config.START_ORIENTATION !== orientation ? orientationHandler.show() : orientationHandler.hide();
            Config.onChange.add((name, value) =>
            {
                console.log('onChange', name, value, Config.START_ORIENTATION);

                if (name !== 'ORIENTATION')
                {
                    return;
                }

                Config.START_ORIENTATION !== value ? orientationHandler.show() : orientationHandler.hide();
            });
        }
    }

    clean()
    {
        [
            this._panelMenu, this._topButtonsPanel,
            this._winPanel, this._footer, this._playButton, this._controlPanel
        ]
            .filter((control) => !!control && typeof control.destroy === 'function')
            .forEach((control) => control.destroy());
    }

    setAutospins(num = 1)
    {
        switch (Config.layout)
        {
            case Config.DESKTOP:
            case Config.PORTRAIT:
                this._controlPanel.autoSpins = num;
                break;

            case Config.LANDSCAPE:
                this._autospinSpinner.value = num;
                break;
        }
    }

    enablePlayButton()
    {
        switch (Config.layout)
        {
            case Config.DESKTOP:
                this._controlPanel.enablePlayButton();
                break;

            case Config.PORTRAIT:
                this._controlPanel.enablePlayButton();
                break;

            case Config.LANDSCAPE:
                this._playButton.enablePlayButton();
                break;
        }
    }

    enablePauseButton()
    {
        switch (Config.layout)
        {
            case Config.DESKTOP:
                this._controlPanel.enablePauseButton();
                break;

            case Config.PORTRAIT:
                this._controlPanel.enablePauseButton();
                break;

            case Config.LANDSCAPE:
                this._playButton.enablePauseButton();
                break;
        }
    }

    lock(canPause = false)
    {
        switch (Config.layout)
        {
            case Config.DESKTOP:
                this._controlPanel.lock(canPause);
                break;

            case Config.PORTRAIT:
                this._controlPanel.lock(canPause);
                break;

            case Config.LANDSCAPE:
                this._playButton.lock(canPause);
                this._panelMenu.lock(canPause);
                this._betSpinner.enabled = false;
                this._autospinSpinner.enabled = false;
                break;
        }
    }

    unlock(hasAutospins)
    {
        switch (Config.layout)
        {
            case Config.DESKTOP:
                this._controlPanel.unlock(hasAutospins);
                break;

            case Config.PORTRAIT:
                this._controlPanel.unlock(hasAutospins);
                break;

            case Config.LANDSCAPE:
                this._playButton.unlock();
                this._panelMenu.unlock();
                this._betSpinner.enabled = !hasAutospins;
                this._autospinSpinner.enabled = !hasAutospins;
                break;
        }
    }

    reload()
    {
        const right = 20;
        const top = 10;
        const bottom = 10;
        const model = this._gameController.model;

        let prevStates = {};
        switch (Config.layout)
        {
            case Config.LANDSCAPE:
                prevStates = {
                    autoSpinStepperValue: this._controlPanel ? this._controlPanel.autoSpins : model.autoSpins,
                    autoSpinStepperEnabled: this._controlPanel ? this._controlPanel._autospinsStepper.enabled : true,
                    betStepperEnabled: this._controlPanel ? this._controlPanel._stakeStepper.enabled : true,
                    buttonPlayEnabled: this._controlPanel ? this._controlPanel._playButton.enabled : true,
                    buttonPlayVisible: this._controlPanel ? this._controlPanel._playButton.visible: true,
                    buttonPauseEnabled: this._controlPanel ? this._controlPanel._pauseButton.enabled : true
                };
                break;

            case Config.PORTRAIT:
                prevStates = {
                    autoSpinStepperValue: this._autospinSpinner ? this._autospinSpinner.value : model.autoSpins,
                    autoSpinStepperEnabled: this._autospinSpinner ? this._autospinSpinner.enabled : true,
                    betStepperEnabled: this._betSpinner ? this._betSpinner.enabled : true,
                    buttonPlayEnabled: this._playButton ? this._playButton._playButton.enabled : true,
                    buttonPlayVisible: this._playButton ? this._playButton._playButton.visible: true,
                    buttonPauseEnabled: this._playButton ? this._playButton._pauseButton.enabled : true
                };
                break;
        }

        this.clean();


        let bg = this._background;
        if (!this._background)
        {
            bg = this._background = this.add.image(0, 0, 'game_background');
        }
        else
        {
            let currentBgKey = this._background.key;
            this._background.destroy();

            if (/bonus/i.test(currentBgKey))
            {
                bg = this._background = this.add.image(0, 0, 'game_bonus_background');
            }
            else
            {
                bg = this._background = this.add.image(0, 0, 'game_background');
            }
        }
        bg.width = Config.GAME_WIDTH;
        bg.height = Config.GAME_HEIGHT;
        
        // splash
        console.log('~~ Config.layout : ', Config.layout);
        if (this._ornament && this._ornament.parent)
        {
            this._ornament.parent.remove(this._ornament, true);
        }

        const ornament = this._ornament = new Phaser.Image(this.game, 0, 0, 'game_background_ornament');
        ornament.width = Config.GAME_WIDTH;
        ornament.y = Config.GAME_HEIGHT - ornament.height - 40;
        this.world.addChild(ornament);

        const logo = this._logo = this._logo || this.add.image(0, 0, 'logo');
        logo.anchor.x = 0.5;

        const winPanelValue = this._winPanel && this._winPanel.value;
        const winPanel = this._winPanel = new WinPanel(this.game);
        winPanel.position.set(
            Config.GAME_WIDTH - winPanel.width - right - 12,
            top
        );
        winPanel.value = winPanelValue;

        let slots = this._slots = this._slots || new Slots(this.game, { cellModels: model.cellModels });
        this._slots.scale.set(1);
        slots.position.set(
            Config.GAME_WIDTH - slots.width >> 1,
            77
        );

        let footer = this._footer = new Footer(this.game, { balance: model.balance, bet: model.stake });
        footer.y = Config.GAME_HEIGHT - footer.height;

        switch (Config.layout)
        {
            case Config.DESKTOP:
            {
                let controlPanel = this._controlPanel = new ControlPanel(this.game, {
                    availableStakes: model.availableStakes,
                    stake: model.stake,
                    availableAutoSpins: model.availableAutoSpins,
                    autoSpins: model.autoSpins
                });

                controlPanel.position.set(
                    (Config.GAME_WIDTH - controlPanel.width >> 1) - controlPanel.getLocalBounds().x,
                    Config.GAME_HEIGHT - controlPanel.height - bottom - 30 - controlPanel.getLocalBounds().y
                );

                controlPanel.onAction.add((panel, action, value) =>
                {
                    SoundManager.playSound('sounds/touch');

                    switch (action)
                    {
                        case 'play':
                            this.onPlay.dispatch();
                            break;

                        case 'pause':
                            this.onStopAutoSpins.dispatch();
                            break;

                        case 'bet':
                            this.onChangeBet.dispatch(value);
                            break;

                        case 'autospins':
                            this.onChangeAutospins.dispatch(value);
                            break;
                    }
                });

                // Panel contains: [Home, Sound]
                let topButtonsPanel = this._topButtonsPanel = new TopButtonsPanel(this.game);
                topButtonsPanel.position.set(26, 10);
                topButtonsPanel.onAction.add((taget, action, value) =>
                {
                    SoundManager.playSound('sounds/touch');

                    switch (action)
                    {
                        case 'help':
                            this.showHelpPopup();
                            break;

                        case 'home':
                            this.onGoToLobby.dispatch();
                            break;

                        case 'audio':                            
                            SoundManager.enabled = value;
                            break;

                        case 'music':                            
                            SoundManager.music = value;
                            this._gameController._server.publish( leanderGMApi.publications.MUSIC_STATUS_CHANGED_GAME, SoundManager.music );
                            break;

                        case 'sound':                            
                            SoundManager.sound = value;
                            this._gameController._server.publish( leanderGMApi.publications.SOUND_EFFECTS_STATUS_CHANGED_GAME, SoundManager.sound );
                            break;
                    }
                });

                logo.scale.set(1);
                logo.position.set(Config.GAME_WIDTH >> 1, -4);

                let rootContainer = this.world;
                rootContainer.addChild(logo);
            }
                break;

            case Config.LANDSCAPE:
            {
                let playButton = this._playButton = new PlayButton(this.game);
                playButton.position.set(
                    Config.GAME_WIDTH - (playButton.width >> 1) - 16,
                    Config.GAME_HEIGHT - playButton.height + 14
                );
                playButton.onAction.add((panel, action, value) =>
                {
                    SoundManager.playSound('sounds/touch');

                    switch (action)
                    {
                        case 'play':
                            this.onPlay.dispatch();
                            break;

                        case 'pause':
                            this.onStopAutoSpins.dispatch();
                            break;
                    }
                });

                // Control contains a slide menu
                let panelMenu = this._panelMenu = new MenuPanel(this.game);
                panelMenu.onAction.add((panel, action, value) =>
                {
                    SoundManager.playSound('sounds/touch');

                    switch (action)
                    {
                        case 'help':
                            this.showHelpPopup();
                            break;

                        case 'home':
                            this.onGoToLobby.dispatch();
                            break;

                        case 'audio':
                            SoundManager.enabled = value;
                            break;

                        case 'music':
                            SoundManager.music = value;
                            break;

                        case 'sound':
                            SoundManager.sound = value;
                            break;
                    }
                });

                const betSpinner = this._betSpinner = new NumericStepper(this.game, {
                    availableValues: model.availableStakes,
                    value: model.stake,
                    label: LocaleManager.getString('bet'),
                    formatFunction: (value) => Utils.formatMoney(value * Config.NUMBER_OF_LINES),
                    bgImage: 'panels.more-less-bg-long'
                });


                betSpinner.enabled = prevStates.betStepperEnabled;
                betSpinner.onChange.add((target, value) =>
                {
                    this.onChangeBet.dispatch(value);
                    betSpinner.adjustValueText().then((fontSize) =>
                    {
                       this._autospinSpinner.setValueFontSize(fontSize);
                    });
                    SoundManager.playSound('sounds/touch');
                });

                const autospinSpinner = this._autospinSpinner = new VerticalNumericStepper(this.game, {
                    availableValues: model.availableAutoSpins,
                    value: model.autoSpins,
                    formatFunction: (value) => '' + Math.floor(value),
                    label: LocaleManager.getString('autoplay.wrap')
                });
                autospinSpinner.value = prevStates.autoSpinStepperValue;
                autospinSpinner.enabled = prevStates.autoSpinStepperEnabled;
                autospinSpinner.onChange.add((target, value) =>
                {
                    this.onChangeAutospins.dispatch(value);
                    SoundManager.playSound('sounds/touch');
                });
                autospinSpinner._labelText.anchor.x = 0.5;
                autospinSpinner._labelText.position.set(
                    autospinSpinner._bg.height >> 1,
                    autospinSpinner.getLocalBounds().height + 5
                );

                autospinSpinner.position.set(
                    Config.GAME_WIDTH - autospinSpinner.getLocalBounds().width - 18,
                    85
                );

                betSpinner.adjustValueText().then((fontSize) =>
                {
                    this._autospinSpinner.setValueFontSize(fontSize);
                });

                betSpinner.position.set(10, Config.GAME_HEIGHT - footer.height - betSpinner.height - 7);
                betSpinner._labelText.anchor.x = 0;
                betSpinner._labelText.position.set(
                    16,
                    -autospinSpinner._labelText.height + 20
                );

                const localeSettings = {
                    'cs': { y: -13 },
                    'fi': { y: -18 },
                    'hu': { y: -12 },
                    'lv': { y: -12 },
                    'pt': { y: -8 },
                    'sr': { y: -8 },
                    'tr': { y: -8 },
                }[LocaleManager.locale] || { y: 0 };
                betSpinner._labelText.y += localeSettings.y;

                slots.position.set(90, 56);

                winPanel.position.set(
                    Config.GAME_WIDTH - winPanel.width - 14,
                    6
                );

                logo.scale.set(0.6);
                logo.position.set(
                    Config.GAME_WIDTH >> 1,
                    0
                );

                const bonusWinPanel = this._bonusWinPanel = new BonusPanel(this.game, {
                    value: 0,
                    label: LocaleManager.getString('bonus_win').toLocaleUpperCase().replace(' ', '\n'),
                    formatFunction: (value) => Utils.formatMoney(value)
                });
                bonusWinPanel.position.set(betSpinner.x - 6, betSpinner.y - 12);
                bonusWinPanel._labelText.anchor.set(0, 1);
                bonusWinPanel._labelText.position.set(10, 12);

                const bonusSpinsPanel = this._bonusSpinsPanel = new BonusPanel(this.game, {
                    value: 0,
                    label: LocaleManager.getString('free_spins').toLocaleUpperCase().replace(' ', '\n'),
                    formatFunction: (value) => '' + value,
                    direction: 'vertical'
                });
                const localeFontSettings = {
                    'de': {
                        fontSize: 12
                    },
                    'el': {
                        fontSize: 12
                    },
                    'fi': {
                        fontSize: 8
                    },
                    'hu': {
                        fontSize: 14
                    },
                    'is': {
                        fontSize: 10
                    },
                    'lv': {
                        fontSize: 12
                    },
                    'sr': {
                        fontSize: 16
                    },
                    'sv': {
                        fontSize: 14
                    },
                    'tr': {
                        fontSize: 10
                    },
                }[LocaleManager.locale];
                if (localeFontSettings)
                {
                    bonusSpinsPanel._labelText.fontSize = localeFontSettings.fontSize;
                }
                bonusSpinsPanel.position.set(autospinSpinner.x - 11, this._slots.y - 6);

                let rootContainer = this.world;
                rootContainer.addChild(this._background);
                rootContainer.addChild(this._ornament);
                rootContainer.addChild(this._slots);
                rootContainer.addChild(this._playButton);
                rootContainer.addChild(this._betSpinner);
                rootContainer.addChild(this._autospinSpinner);
                rootContainer.addChild(bonusWinPanel);
                rootContainer.addChild(bonusSpinsPanel);
                rootContainer.addChild(this._winPanel);
                rootContainer.addChild(this._footer);
                rootContainer.addChild(this._logo);
                rootContainer.addChild(this._panelMenu);

                playButton._playButton.enabled = prevStates.buttonPlayEnabled;
                playButton._playButton.visible = prevStates.buttonPlayVisible;
                playButton._pauseButton.visible = !prevStates.buttonPlayVisible;
                playButton._pauseButton.enabled = prevStates.buttonPauseEnabled;

                this._showBonusPanels(false);
            }
                break;

            case Config.PORTRAIT:
            {
                let controlPanel = this._controlPanel = new MobileControlPanel(this.game, {
                    availableStakes: model.availableStakes,
                    stake: model.stake,
                    availableAutoSpins: model.availableAutoSpins,
                    autoSpins: model.autoSpins
                });
                controlPanel.autoSpins = prevStates.autoSpinStepperValue;
                controlPanel._autospinsStepper.enabled = prevStates.autoSpinStepperEnabled;
                controlPanel._stakeStepper.enabled = prevStates.betStepperEnabled;
                controlPanel._playButton.enabled = prevStates.buttonPlayEnabled;
                controlPanel._playButton.visible = prevStates.buttonPlayVisible;
                controlPanel._pauseButton.visible = !prevStates.buttonPlayVisible;
                controlPanel._pauseButton.enabled = prevStates.buttonPauseEnabled;

                const controlPanelBounds = controlPanel.getLocalBounds();
                controlPanel.position.set(
                    (Config.GAME_WIDTH - controlPanel.width >> 1) - controlPanelBounds.x,
                    Config.GAME_HEIGHT - controlPanel.height - 84 - controlPanelBounds.y
                );

                controlPanel.onAction.add((panel, action, value) =>
                {
                    SoundManager.playSound('sounds/touch');

                    switch (action)
                    {
                        case 'play':
                            this.onPlay.dispatch();
                            break;

                        case 'pause':
                            this.onStopAutoSpins.dispatch();
                            break;

                        case 'bet':
                            this.onChangeBet.dispatch(value);
                            break;

                        case 'autospins':
                            this.onChangeAutospins.dispatch(value);
                            break;
                    }
                });

                // Control contains a slide menu
                let panelMenu = this._panelMenu = new MenuPanel(this.game);
                panelMenu.onAction.add((taget, action, value) =>
                {
console.log('onAction', taget, action, value );                  
                    SoundManager.playSound('sounds/touch');

                    switch (action)
                    {
                        case 'help':
                            this.showHelpPopup();
                            break;

                        case 'home':
                            this.onGoToLobby.dispatch();
                            break;

                        case 'audio':
                            SoundManager.enabled = value;
                            break;

                        case 'music':
                            SoundManager.music = value;
                            break;

                        case 'sound':
                            SoundManager.sound = value;
                            break;
                    }
                });

                slots.scale.set(0.67);
                slots.position.set(5, 290);

                winPanel.position.set(
                    // slots.x + slots.width - winPanel.width,
                    (Config.GAME_WIDTH - winPanel.width) >> 1,
                    slots.y - winPanel.height - 16
                );

                logo.scale.set(1.5);
                logo.position.set(Config.GAME_WIDTH >> 1, 42);

                let rootContainer = this.world;
                rootContainer.addChild(this._background);
                rootContainer.addChild(this._ornament);
                rootContainer.addChild(this._slots);
                rootContainer.addChild(this._controlPanel);
                rootContainer.addChild(this._winPanel);
                rootContainer.addChild(this._footer);
                rootContainer.addChild(this._logo);
                rootContainer.addChild(this._panelMenu);
            }
                break;
        }

        PopupManager.redraw();

        if (Config.debug)
        {
            this._debugPanel = new DebugPanel(this.game);
        }
    }

    reset()
    {

    }

    showBonusBackground()
    {
        let bg = this._background;
        bg.loadTexture('game_bonus_background');
        bg.width = Config.GAME_WIDTH;
        bg.height = Config.GAME_HEIGHT;


        const logo = this._logo;
        logo.scale.set(1);
        logo.y = Config.layout === Config.PORTRAIT ? 52 : 0;
        logo.loadTexture('bonus-round-title');

        this._showBonusPanels(true);

        this._playBonusBgMusic();
    }

    hideBonusBackground()
    {
        let bg = this._background;
        bg.loadTexture('game_background');
        bg.width = Config.GAME_WIDTH;
        bg.height = Config.GAME_HEIGHT;

        const logo = this._logo;
        switch (Config.layout)
        {
            case Config.DESKTOP:
                logo.scale.set(1);
                logo.y = -4;
                break;

            case Config.LANDSCAPE:
                logo.scale.set(0.6);
                logo.y = 0;
                break;

            case Config.PORTRAIT:
                logo.scale.set(1.5);
                logo.y = 42;
                break;
        }
        logo.loadTexture('logo');


        this._showBonusPanels(false);

        this._stopBonusBgMusic();
    }

    showPopup(popupClass, ...rest)
    {
        return new Promise(handler.bind(this));

        function handler(res, rej)
        {
            if (this._orientationHandler && this._orientationHandler.isShown)
            {
                this._orientationHandler.onHide.addOnce(() => show());
            }
            else if (this.game.paused)
            {
                this.game.onResume.addOnce(() => show());
            }
            else
            {
                show();
            }

            function show()
            {
                const popup = PopupManager.show(popupClass);
                popup.show.apply(popup, rest);
                popup.onClose.addOnce(() =>
                {
                    PopupManager.hide(popup);
                    SoundManager.fadeInMusic();
                    res();
                });
            }
        }
    }

    showHelpPopup()
    {
        return this.showPopup(HelpPopup);
    }

    hideHelpPopup()
    {
        PopupManager.hideByName('HelpPopup');
    }

    showBigWinPopup(reward)
    {
        return this.showPopup(BigWinPopup, reward);
    }

    showBonusSpinPopup(spinsCount)
    {
        return this.showPopup(BonusSpinPopup, spinsCount);
    }

    showEndBonusRoundPopup(money)
    {
        return this.showPopup(EndBonusRoundPopup, money);
    }

    set bonusWin(value)
    {
        switch (Config.layout)
        {
            case Config.DESKTOP:
            case Config.PORTRAIT:
                this._controlPanel.bonusWin = value;
                break;

            case Config.LANDSCAPE:
                this._bonusWinPanel.value = value;
                break;
        }
    }

    set bonusSpins(value)
    {
        switch (Config.layout)
        {
            case Config.DESKTOP:
            case Config.PORTRAIT:
                this._controlPanel.bonusSpins = value;
                break;

            case Config.LANDSCAPE:
                this._bonusSpinsPanel.value = value;
                break;
        }
    }

    enableHelpButton(value)
    {
        switch (Config.layout)
        {
            case Config.DESKTOP:
                this._topButtonsPanel.enabled = value;
                break;

            case Config.PORTRAIT:
            case Config.LANDSCAPE:
                this._panelMenu.enabled = value;
                break;
        }
    }

    _showBonusPanels(value)
    {
        this.enableHelpButton(!value);

        switch (Config.layout)
        {
            case Config.DESKTOP:
            case Config.PORTRAIT:
                this._controlPanel.showBonusPanels(value);
                break;

            case Config.LANDSCAPE:
                this._bonusWinPanel.visible = value;
                this._bonusSpinsPanel.visible = value;
                this._betSpinner.visible = !value;
                this._autospinSpinner.visible = !value;

                if (!value)
                {
                    this._bonusWinPanel.value = 0;
                    this._bonusSpinsPanel.value = 0;
                }
                break;
        }
    }

    _stopBonusBgMusic()
    {
        if (this._bonusMusic)
        {
            this._bonusMusic.stop();
            SoundManager.playMusic('music/bg-music');
        }
    }

    _playBonusBgMusic()
    {
        this._bonusMusic = SoundManager.playMusic('music/bonus-round-music');
    }

    setStake(value)
    {
        switch (Config.layout)
        {
            case Config.DESKTOP:
            case Config.PORTRAIT:
                this._controlPanel._stakeStepper.value = value;
                break;

            case Config.LANDSCAPE:
                this._betSpinner.value = value;
                break;
        }
    }
}